import React, { useEffect, useRef, useState, useCallback } from 'react'
import { SoundToggleBtn } from '../ui/SoundToggleBtn'
import { fmtCoins } from '../../lib/cards'
import { io, Socket } from 'socket.io-client'
import { Sounds } from '../../lib/sounds'

interface Props {
  token:string; balance:number|null; playerId?:string; operatorId?:string
}
type Phase   = 'betting'|'live'|'result'
type Direction = 'buy'|'sell'

interface HistoryItem { direction:'up'|'down'; start:number; end:number; pct:number }

// ─── Scrolling live chart — like Evolution Stock Market ───────────────────────
const VISIBLE_SECS = 20      // seconds of data visible
const TICK_SEC     = 0.2     // server tick every 200ms

function PriceChart({prices,phase,direction,startPrice,lastTickTime}:{
  prices:number[]; phase:Phase; direction:'up'|'down'|null
  startPrice:number; lastTickTime:number
}){
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const rafRef    = useRef(0)

  // Resize once on mount + window resize
  useEffect(()=>{
    const cvs=canvasRef.current; if(!cvs) return
    const ro=new ResizeObserver(()=>{
      cvs.width=cvs.clientWidth; cvs.height=cvs.clientHeight
    })
    ro.observe(cvs); cvs.width=cvs.clientWidth; cvs.height=cvs.clientHeight
    return()=>ro.disconnect()
  },[])

  useEffect(()=>{
    const cvs=canvasRef.current; if(!cvs) return
    const ctx=cvs.getContext('2d')!

    const draw=()=>{
      rafRef.current=requestAnimationFrame(draw)
      const W=cvs.width, H=cvs.height
      if(!W||!H) return
      ctx.clearRect(0,0,W,H)

      const pts=prices
      if(pts.length<2){ return }

      // How far we are between last tick and next (0→1)
      const tickElapsed=(Date.now()-lastTickTime)/1000
      const tickFrac=Math.min(tickElapsed/TICK_SEC,1)

      // Pixels per second of data
      const pxPerSec=W/VISIBLE_SECS
      const pxPerTick=pxPerSec*TICK_SEC

      // Total pixels for all data points
      // Current time is always at x = W (right edge)
      // Each past tick is pxPerTick further left
      // Scroll: tickFrac moves continuously

      const lineColor=phase==='result'
        ? direction==='up'?'#22c55e':'#ef4444'
        : '#3b82f6'

      // Build point coordinates — scroll left continuously
      // Right edge = current interpolated position
      const coords: [number,number][] = []

      // Add interpolated current price (between last two pts)
      const allPts=[...pts]
      const interpPrice=pts.length>=2
        ? pts[pts.length-2]+(pts[pts.length-1]-pts[pts.length-2])*tickFrac
        : pts[pts.length-1]

      // x positions: newest point at W, each prev tick pxPerTick further left
      // with continuous scroll offset
      for(let i=0;i<allPts.length;i++){
        const stepsFromEnd=allPts.length-1-i
        const x=W - stepsFromEnd*pxPerTick - tickFrac*pxPerTick
        coords.push([x, allPts[i]])
      }
      // Add interpolated current point at right edge
      coords.push([W, interpPrice])

      // Filter to visible range (x >= -20 for smooth entry)
      const visible=coords.filter(([x])=>x>=-20)
      if(visible.length<2) return

      // Price range
      const ys=visible.map(([,y])=>y)
      const mn=Math.min(...ys), mx=Math.max(...ys)
      const pad=Math.max((mx-mn)*0.3, 1.5)
      const lo=mn-pad, hi=mx+pad
      const sy=(v:number)=>H-((v-lo)/(hi-lo))*(H*0.82)-H*0.09

      // Dashed baseline (start price)
      if(startPrice>=lo&&startPrice<=hi){
        const spY=sy(startPrice)
        ctx.save()
        ctx.setLineDash([6,5])
        ctx.strokeStyle='rgba(255,255,255,.15)'
        ctx.lineWidth=1
        ctx.beginPath(); ctx.moveTo(0,spY); ctx.lineTo(W,spY); ctx.stroke()
        ctx.restore()
      }

      // Smooth bezier path builder
      const buildPath=()=>{
        ctx.moveTo(visible[0][0], sy(visible[0][1]))
        for(let i=1;i<visible.length;i++){
          const [x0,y0v]=[visible[i-1][0], sy(visible[i-1][1])]
          const [x1,y1v]=[visible[i][0],   sy(visible[i][1])]
          const cpx=(x0+x1)/2
          ctx.bezierCurveTo(cpx,y0v, cpx,y1v, x1,y1v)
        }
      }

      // Fill gradient
      const grad=ctx.createLinearGradient(0,0,0,H)
      grad.addColorStop(0, lineColor+'35')
      grad.addColorStop(0.7,'rgba(0,0,0,0)')
      ctx.beginPath(); buildPath()
      ctx.lineTo(W,H); ctx.lineTo(visible[0][0],H); ctx.closePath()
      ctx.fillStyle=grad; ctx.fill()

      // Main line
      ctx.beginPath(); buildPath()
      ctx.strokeStyle=lineColor; ctx.lineWidth=2.8
      ctx.lineJoin='round'; ctx.lineCap='round'
      ctx.stroke()

      // Glowing dot at right edge (current price)
      const dotX=W, dotY=sy(interpPrice)
      const pulse=0.5+0.5*Math.sin(Date.now()*.008)
      ctx.beginPath(); ctx.arc(dotX,dotY,10+pulse*4,0,Math.PI*2)
      ctx.fillStyle=lineColor+'22'; ctx.fill()
      ctx.beginPath(); ctx.arc(dotX,dotY,5,0,Math.PI*2)
      ctx.fillStyle='#fff'; ctx.fill()
      ctx.beginPath(); ctx.arc(dotX,dotY,3,0,Math.PI*2)
      ctx.fillStyle=lineColor; ctx.fill()

      // Price label at right
      ctx.fillStyle='rgba(0,0,0,.6)'
      ctx.fillRect(W-58,dotY-10,56,20)
      ctx.fillStyle=lineColor
      ctx.font='bold 11px system-ui'
      ctx.textAlign='right'
      ctx.fillText(interpPrice.toFixed(2),W-4,dotY+4)
    }

    rafRef.current=requestAnimationFrame(draw)
    return()=>cancelAnimationFrame(rafRef.current)
  },[prices,phase,direction,startPrice,lastTickTime])

  return <canvas ref={canvasRef} style={{width:'100%',height:'100%',display:'block'}}/>
}

// ─── City skyline background ──────────────────────────────────────────────────
function CitySkyline(){
  const buildings=[
    {x:0,w:40,h:80},{x:38,w:25,h:120},{x:61,w:35,h:90},
    {x:94,w:20,h:140},{x:112,w:45,h:100},{x:155,w:30,h:160},
    {x:183,w:50,h:110},{x:231,w:25,h:130},{x:254,w:40,h:85},
    {x:292,w:35,h:145},{x:325,w:30,h:100},{x:353,w:45,h:120},
    {x:396,w:30,h:90},{x:424,w:50,h:140},
  ]
  return(
    <svg viewBox="0 0 480 180" xmlns="http://www.w3.org/2000/svg"
      style={{position:'absolute',bottom:0,left:0,right:0,width:'100%',opacity:.35}}>
      {buildings.map((b,i)=>(
        <rect key={i} x={b.x} y={180-b.h} width={b.w} height={b.h}
          fill={`rgba(${30+i*5},${20+i*3},${60+i*4},0.9)`}/>
      ))}
      {/* Windows */}
      {buildings.map((b,i)=>
        Array.from({length:Math.floor(b.h/18)},(_,r)=>
          Array.from({length:Math.floor(b.w/12)},(_,c)=>(
            <rect key={`${i}-${r}-${c}`}
              x={b.x+c*12+3} y={180-b.h+r*18+4}
              width={5} height={8}
              fill={Math.random()>.4?'rgba(255,230,100,0.7)':'rgba(100,150,255,0.3)'}/>
          ))
        )
      )}
    </svg>
  )
}

// ─── Main component ───────────────────────────────────────────────────────────
export function StockGame({token,balance:initBalance,playerId:initPid,operatorId:initOid}:Props){
  const socketRef  = useRef<Socket|null>(null)
  const sessionRef = useRef({playerId:initPid||'',operatorId:initOid||''})

  const [phase,setPhase]           = useState<Phase>('betting')
  const [balance,setBalance]       = useState<number|null>(initBalance)
  const [prices,setPrices]         = useState<number[]>([100])
  const [startPrice,setStartPrice] = useState(100)
  const [currentPrice,setCurrentPrice] = useState(100)
  const [bettingMs,setBettingMs]   = useState(5000)
  const [countdown,setCountdown]   = useState(5)
  const [history,setHistory]       = useState<HistoryItem[]>([])
  const [resultDir,setResultDir]   = useState<'up'|'down'|null>(null)
  const [myBet,setMyBet]           = useState<{stake:number;direction:Direction}|null>(null)
  const [myResult,setMyResult]     = useState<{win:boolean;payout:number}|null>(null)
  const [stake,setStake]           = useState(20)
  const [autoPlay,setAutoPlay]     = useState(false)
  const [lastDir,setLastDir]       = useState<Direction|null>(null)
  const [toast,setToast]           = useState('')
  const [currency,setCurrency]     = useState('coins')
  const [lastTickTime,setLastTickTime] = useState(Date.now())
  const countdownRef = useRef<any>(null)

  useEffect(()=>{ if(initBalance!==null) setBalance(initBalance) },[initBalance])
  const showToast=(msg:string,d=2500)=>{ setToast(msg); setTimeout(()=>setToast(''),d) }

  const priceChange = currentPrice - startPrice
  const changePct   = startPrice ? Math.round((priceChange/startPrice)*10000)/100 : 0
  const isUp        = priceChange >= 0

  // ── Socket ──────────────────────────────────────────────────────────────────
  useEffect(()=>{
    const GS=(import.meta as any).env?.VITE_GAME_SERVER_URL||'http://localhost:3002'
    const s=io(GS,{transports:['polling'],auth:{token}})
    socketRef.current=s
    try{
      const p=JSON.parse(atob(token.split('.')[1]))
      sessionRef.current={playerId:p.sub||'',operatorId:p.operator_id||''}
      if(p.currency) setCurrency(p.currency)
    }catch{}

    s.on('connect',async()=>{
      try{
        const {playerId}=sessionRef.current
        const r=await fetch(`${GS}/wallet/balance?player_id=${playerId}`)
        const d=await r.json(); if(typeof d.balance==='number') setBalance(d.balance)
      }catch{}
    })

    s.on('stock:round',(d:any)=>{
      setPhase(d.phase||'betting')
      setStartPrice(d.start_price); setCurrentPrice(d.start_price)
      setPrices(d.prices||[d.start_price])
      setBettingMs(d.betting_ms||5000)
      setResultDir(null); setMyBet(null); setMyResult(null)
      if(d.history) setHistory(d.history)
      // Countdown
      clearInterval(countdownRef.current)
      let cd=Math.ceil((d.betting_ms||5000)/1000)
      setCountdown(cd)
      if(d.phase==='betting'){
        countdownRef.current=setInterval(()=>{
          cd--; setCountdown(Math.max(0,cd))
          if(cd<=0) clearInterval(countdownRef.current)
        },1000)
      }
    })

    s.on('stock:live',()=>{
      setPhase('live')
      clearInterval(countdownRef.current)
    })

    s.on('stock:tick',(d:any)=>{
      setPrices(d.prices||[])
      setCurrentPrice(d.price)
      setLastTickTime(Date.now())
    })

    s.on('stock:result',(d:any)=>{
      setPhase('result')
      setResultDir(d.direction)
      setPrices(d.prices||[])
      setCurrentPrice(d.end_price)
      setHistory(h=>[{
        direction:d.direction, start:d.start_price,
        end:d.end_price, pct:d.change_pct
      },...h].slice(0,20))
    })

    s.on('stock:bet_placed',(d:any)=>{
      setMyBet({stake:d.stake,direction:d.direction})
      showToast(`${d.direction==='buy'?'📈 BUY':'📉 SELL'} ₹${d.stake} placed!`,1800)
      Sounds.go()
    })

    s.on('stock:my_result',(d:any)=>{
      setMyResult({win:d.win,payout:d.payout})
      if(typeof d.balance==='number') setBalance(d.balance)
      if(d.win){ Sounds.cashout(); showToast(`🏆 WIN ₹${d.payout.toFixed(2)}!`,3000) }
      else {     Sounds.cooked();  showToast(`💔 Wrong direction`,2000) }
      // Auto play
      if(autoPlay && lastDir){
        setTimeout(()=>{ /* will auto-bet on next round */ },500)
      }
    })

    s.on('stock:error',(d:any)=>{ showToast(`❌ ${d.error}`) })
    s.on('balance',(d:any)=>{ if(typeof d.balance==='number') setBalance(d.balance) })
    return()=>{ s.disconnect(); clearInterval(countdownRef.current) }
  },[token])

  // Auto play — place bet automatically on new betting phase
  useEffect(()=>{
    if(!autoPlay||!lastDir||phase!=='betting'||myBet) return
    const t=setTimeout(()=>{ placeBet(lastDir) },1000)
    return()=>clearTimeout(t)
  },[autoPlay,lastDir,phase,myBet])

  const placeBet=useCallback((dir:Direction)=>{
    const s=socketRef.current; const {playerId,operatorId}=sessionRef.current
    if(!s||phase!=='betting'||myBet) return
    if(balance!==null&&balance<stake) return showToast('Not enough balance')
    setBalance(b=>b!==null?Math.max(0,b-stake):b)
    setLastDir(dir)
    s.emit('stock:bet',{player_id:playerId,operator_id:operatorId,stake,direction:dir})
  },[phase,myBet,balance,stake])

  const adjustStake=(delta:number)=>{
    setStake(s=>Math.max(1,Math.min(10000,s+delta)))
  }

  return(
    <div style={{
      display:'flex',flexDirection:'column',height:'100%',
      background:'linear-gradient(180deg,#0d0b2a 0%,#1a1040 40%,#0d0b2a 100%)',
      color:'#fff',fontFamily:'system-ui,sans-serif',
      userSelect:'none',overflow:'hidden',position:'relative',
    }}>
      <style>{`
        @keyframes sToast{0%{opacity:0;transform:translateX(-50%) scale(.85)}15%,85%{opacity:1;transform:translateX(-50%) scale(1)}100%{opacity:0}}
        @keyframes sTick{0%,100%{opacity:1}50%{opacity:.5}}
        @keyframes sWin{0%{transform:scale(.8);opacity:0}60%{transform:scale(1.1)}100%{transform:scale(1);opacity:1}}
        @keyframes sBlink{0%,100%{opacity:1}50%{opacity:.3}}
        .sbtn:hover{filter:brightness(1.1)}.sbtn:active{transform:scale(.95)!important}
      `}</style>

      {/* Header */}
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',
        padding:'8px 14px',background:'rgba(0,0,0,.5)',
        borderBottom:'1px solid rgba(255,255,255,.07)',flexShrink:0,zIndex:10}}>
        <div style={{display:'flex',alignItems:'center',gap:8}}>
          {/* Logo */}
          <div style={{display:'flex',alignItems:'center',gap:5}}>
            <div style={{fontSize:20}}>📈</div>
            <div>
              <div style={{fontSize:11,fontWeight:900,color:'#60a5fa',letterSpacing:.5,lineHeight:1}}>
                STOCK
              </div>
              <div style={{fontSize:9,color:'#a78bfa',letterSpacing:1}}>TRADE</div>
            </div>
          </div>
          {/* Phase badge */}
          <div style={{
            fontSize:9,fontWeight:700,padding:'2px 8px',borderRadius:10,
            background:phase==='betting'?'rgba(245,158,11,.2)':
              phase==='live'?'rgba(34,197,94,.2)':'rgba(99,102,241,.2)',
            color:phase==='betting'?'#fbbf24':phase==='live'?'#4ade80':'#a5b4fc',
            border:`1px solid ${phase==='betting'?'rgba(245,158,11,.4)':phase==='live'?'rgba(34,197,94,.4)':'rgba(99,102,241,.4)'}`,
            animation:phase==='live'?'sBlink 1.5s infinite':'none',
          }}>
            {phase==='betting'?`BET ${countdown}s`:phase==='live'?'● LIVE':'RESULT'}
          </div>
        </div>
        <div style={{display:'flex',alignItems:'center',gap:8}}>
          <SoundToggleBtn/>
          <div style={{background:'rgba(96,165,250,.1)',border:'1px solid rgba(96,165,250,.3)',
            borderRadius:8,padding:'3px 10px',fontWeight:700,color:'#60a5fa',fontSize:12}}>
            {balance!==null?`₹${fmtCoins(balance)}`:'—'}
          </div>
        </div>
      </div>

      {/* Toast */}
      {toast&&(
        <div style={{position:'absolute',top:54,left:'50%',zIndex:60,
          animation:'sToast 2.5s ease-in-out forwards',pointerEvents:'none',
          background:'rgba(0,0,0,.92)',color:'#fff',padding:'7px 18px',
          borderRadius:20,fontSize:13,fontWeight:700,whiteSpace:'nowrap',
          border:'1px solid rgba(255,255,255,.15)'}}>
          {toast}
        </div>
      )}

      {/* ── Chart area ─────────────────────────────────────────────────────── */}
      <div style={{flex:1,position:'relative',overflow:'hidden',minHeight:0}}>
        <CitySkyline/>

        {/* Price info bar */}
        <div style={{position:'absolute',top:8,left:12,zIndex:5,
          display:'flex',alignItems:'center',gap:8}}>
          <div style={{
            fontSize:20,fontWeight:900,
            color:isUp?'#4ade80':'#f87171',
            textShadow:`0 0 12px ${isUp?'rgba(74,222,128,.5)':'rgba(248,113,113,.5)'}`,
          }}>
            {currentPrice.toFixed(2)}
          </div>
          <div style={{
            fontSize:11,fontWeight:700,padding:'2px 7px',borderRadius:8,
            background:isUp?'rgba(74,222,128,.15)':'rgba(248,113,113,.15)',
            color:isUp?'#4ade80':'#f87171',
          }}>
            {isUp?'▲':'▼'} {Math.abs(changePct).toFixed(2)}%
          </div>
        </div>

        {/* History dots (top right) */}
        <div style={{position:'absolute',top:10,right:12,zIndex:5,display:'flex',gap:3}}>
          {history.slice(0,8).map((h,i)=>(
            <div key={i} style={{
              width:10,height:10,borderRadius:'50%',
              background:h.direction==='up'?'#4ade80':'#f87171',
              opacity:1-i*.1,
            }}/>
          ))}
        </div>

        {/* Chart canvas */}
        <div style={{position:'absolute',inset:0,zIndex:2}}>
          <PriceChart prices={prices} phase={phase}
            direction={resultDir} startPrice={startPrice} lastTickTime={lastTickTime}/>
        </div>

        {/* Result overlay */}
        {phase==='result'&&resultDir&&(
          <div style={{position:'absolute',top:'50%',left:'50%',
            transform:'translate(-50%,-50%)',zIndex:10,textAlign:'center',
            animation:'sWin .4s ease-out',pointerEvents:'none'}}>
            <div style={{
              fontSize:28,fontWeight:900,
              color:resultDir==='up'?'#4ade80':'#f87171',
              textShadow:`0 0 30px ${resultDir==='up'?'rgba(74,222,128,.8)':'rgba(248,113,113,.8)'}`,
            }}>
              {resultDir==='up'?'📈 UP':'📉 DOWN'}
            </div>
            {myResult&&(
              <div style={{
                fontSize:14,fontWeight:800,marginTop:4,
                color:myResult.win?'#4ade80':'rgba(255,255,255,.5)',
              }}>
                {myResult.win?`WIN ₹${myResult.payout.toFixed(2)}`:'No Win'}
              </div>
            )}
          </div>
        )}

        {/* My bet indicator */}
        {myBet&&phase==='live'&&(
          <div style={{position:'absolute',bottom:10,left:'50%',
            transform:'translateX(-50%)',zIndex:10,
            display:'flex',alignItems:'center',gap:6,
            background:'rgba(0,0,0,.7)',border:'1px solid rgba(255,255,255,.15)',
            borderRadius:20,padding:'4px 12px',}}>
            <span style={{fontSize:12,
              color:myBet.direction==='buy'?'#4ade80':'#f87171',fontWeight:800}}>
              {myBet.direction==='buy'?'📈 BUY':'📉 SELL'} ₹{myBet.stake}
            </span>
            <span style={{fontSize:10,color:'rgba(255,255,255,.4)'}}>→ ₹{(myBet.stake*1.94).toFixed(2)}</span>
          </div>
        )}
      </div>

      {/* ── Controls ───────────────────────────────────────────────────────── */}
      <div style={{
        background:'linear-gradient(to top,rgba(0,0,0,.98),rgba(0,0,0,.8))',
        borderTop:'1px solid rgba(255,255,255,.07)',
        padding:'8px 12px 12px',flexShrink:0,
      }}>
        {/* BUY / SELL buttons */}
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,marginBottom:10}}>
          <button className="sbtn" onClick={()=>placeBet('sell')}
            disabled={phase!=='betting'||!!myBet}
            style={{
              padding:'14px 0',borderRadius:10,border:'none',
              cursor:phase==='betting'&&!myBet?'pointer':'default',
              fontWeight:900,fontSize:17,transition:'all .15s',
              background:phase==='betting'&&!myBet
                ?'linear-gradient(135deg,#dc2626,#991b1b)'
                :'rgba(220,38,38,.2)',
              color:phase==='betting'&&!myBet?'#fff':'rgba(255,255,255,.25)',
              boxShadow:phase==='betting'&&!myBet?'0 4px 16px rgba(220,38,38,.4)':'none',
            }}>
            ↓ SELL
          </button>
          <button className="sbtn" onClick={()=>placeBet('buy')}
            disabled={phase!=='betting'||!!myBet}
            style={{
              padding:'14px 0',borderRadius:10,border:'none',
              cursor:phase==='betting'&&!myBet?'pointer':'default',
              fontWeight:900,fontSize:17,transition:'all .15s',
              background:phase==='betting'&&!myBet
                ?'linear-gradient(135deg,#16a34a,#15803d)'
                :'rgba(22,163,74,.2)',
              color:phase==='betting'&&!myBet?'#fff':'rgba(255,255,255,.25)',
              boxShadow:phase==='betting'&&!myBet?'0 4px 16px rgba(22,163,74,.4)':'none',
            }}>
            ↑ BUY
          </button>
        </div>

        {/* Stake + Auto Play row */}
        <div style={{display:'flex',alignItems:'center',gap:8}}>
          {/* Auto play toggle */}
          <div style={{display:'flex',alignItems:'center',gap:6,flexShrink:0}}>
            <div onClick={()=>setAutoPlay(a=>!a)}
              style={{
                width:40,height:22,borderRadius:11,cursor:'pointer',
                background:autoPlay?'#7c3aed':'rgba(255,255,255,.15)',
                position:'relative',transition:'background .2s',
              }}>
              <div style={{
                position:'absolute',top:3,
                left:autoPlay?20:3,width:16,height:16,
                borderRadius:'50%',background:'#fff',
                transition:'left .2s',
              }}/>
            </div>
            <span style={{fontSize:9,color:'rgba(255,255,255,.4)',lineHeight:1.2}}>
              AUTO<br/>PLAY
            </span>
          </div>

          {/* Stake - amount + */}
          <div style={{flex:1,display:'flex',alignItems:'center',gap:6,
            background:'rgba(255,255,255,.07)',borderRadius:10,padding:'6px 10px'}}>
            <button onClick={()=>adjustStake(-stake>=10?-10:-1)}
              style={{width:28,height:28,borderRadius:7,border:'none',
                background:'rgba(255,255,255,.1)',color:'#fff',
                fontSize:16,fontWeight:700,cursor:'pointer',flexShrink:0}}>
              −
            </button>
            <div style={{flex:1,textAlign:'center'}}>
              <div style={{fontSize:14,fontWeight:800,color:'#fff'}}>
                ₹{stake.toLocaleString()}
              </div>
              <div style={{fontSize:9,color:'rgba(255,255,255,.3)'}}>
                Win: ₹{(stake*1.94).toFixed(2)}
              </div>
            </div>
            <button onClick={()=>adjustStake(stake>=10?10:1)}
              style={{width:28,height:28,borderRadius:7,border:'none',
                background:'rgba(255,255,255,.1)',color:'#fff',
                fontSize:16,fontWeight:700,cursor:'pointer',flexShrink:0}}>
              +
            </button>
          </div>
        </div>

        {/* Quick stake chips */}
        <div style={{display:'flex',gap:4,marginTop:7,justifyContent:'center'}}>
          {[5,10,20,50,100,500].map(sv=>(
            <button key={sv} className="sbtn"
              onClick={()=>setStake(sv)}
              style={{
                borderRadius:14,padding:'3px 8px',cursor:'pointer',
                border:stake===sv?'1.5px solid #60a5fa':'1.5px solid rgba(255,255,255,.1)',
                background:stake===sv?'rgba(96,165,250,.18)':'rgba(255,255,255,.05)',
                color:stake===sv?'#60a5fa':'rgba(255,255,255,.4)',
                fontSize:10,fontWeight:700,transition:'all .1s',
              }}>
              ₹{sv>=1000?`${sv/1000}K`:sv}
            </button>
          ))}
        </div>

        {/* Payout info */}
        <div style={{display:'flex',justifyContent:'space-between',marginTop:6,
          padding:'0 2px',fontSize:9,color:'rgba(255,255,255,.25)'}}>
          <span>Payout: 1.94× on correct direction</span>
          <span>Last 100: {history.slice(0,100).filter(h=>h.direction==='up').length}/
            {Math.min(history.length,100)} UP</span>
        </div>
      </div>
    </div>
  )
}
